import { ethers } from 'ethers';
import { Web3Provider } from '@ethersproject/providers';
import { NetworkManager } from './NetworkManager';

export class ProviderManager {
    static instance = null;
    provider = null;
    signer = null;
    switchingChain = false;

    static getInstance() {
        if (!ProviderManager.instance) {
            ProviderManager.instance = new ProviderManager();
        }
        return ProviderManager.instance;
    }

    async initialize() {
        if (typeof window.ethereum !== 'undefined') {
            this.provider = new Web3Provider(window.ethereum);
            await this.validateAndSwitchNetwork();
            this.signer = this.provider.getSigner();

            window.ethereum.on('chainChanged', () => {
                window.location.reload();
            });

            return true;
        }
        throw new Error('Web3 provider not found');
    }

    async waitForChainChange(targetChainId, timeout = 15000) {
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                window.ethereum.removeListener('chainChanged', handleChainChange);
                this.switchingChain = false;
                reject(new Error('Network switch timeout'));
            }, timeout);

            const handleChainChange = async (chainIdHex) => {
                const newChainId = parseInt(chainIdHex, 16);
                if (newChainId === targetChainId) {
                    clearTimeout(timeoutId);
                    window.ethereum.removeListener('chainChanged', handleChainChange);
                    // Small delay to ensure the provider is ready
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    this.switchingChain = false;
                    resolve();
                }
            };

            // Check if already on correct chain
            const currentChainId = window.ethereum.chainId;
            if (parseInt(currentChainId, 16) === targetChainId) {
                clearTimeout(timeoutId);
                this.switchingChain = false;
                resolve();
                return;
            }

            window.ethereum.on('chainChanged', handleChainChange);
        });
    }

    async validateAndSwitchNetwork() {
        if (!this.provider) {
            throw new Error('Provider not initialized');
        }

        // Prevent multiple simultaneous switch attempts
        if (this.switchingChain) {
            throw new Error('Network switch already in progress');
        }

        const network = await this.provider.getNetwork();
        const targetNetwork = NetworkManager.getTargetNetwork();

        if (network.chainId !== targetNetwork.chainId) {
            this.switchingChain = true;

            try {
                // First try to switch to the network
                try {
                    await window.ethereum.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: ethers.utils.hexValue(targetNetwork.chainId) }],
                    });
                } catch (switchError) {
                    // Chain doesn't exist yet
                    if (switchError.code === 4902) {
                        await window.ethereum.request({
                            method: 'wallet_addEthereumChain',
                            params: [{
                                chainId: ethers.utils.hexValue(targetNetwork.chainId),
                                chainName: targetNetwork.name,
                                nativeCurrency: targetNetwork.nativeCurrency,
                                rpcUrls: targetNetwork.rpcUrls || [targetNetwork.rpcUrl],
                                blockExplorerUrls: [targetNetwork.explorerUrl]
                            }]
                        });
                    } else {
                        throw switchError;
                    }
                }

                // Wait for the chain change to complete
                await this.waitForChainChange(targetNetwork.chainId);

                // Reinitialize provider
                this.provider = new Web3Provider(window.ethereum);
                return true;

            } catch (error) {
                this.switchingChain = false;

                if (error.code === 4001) {
                    throw new Error('User rejected network switch');
                }

                if (error.code === 4902) {
                    throw new Error(`${targetNetwork.name} network not configured in wallet`);
                }

                console.error('Chain switch error:', error);
                throw new Error(`Failed to switch to ${targetNetwork.name}. Please add the network manually.`);
            }
        }
        return true;
    }

    getProvider() {
        if (!this.provider) {
            throw new Error('Provider not initialized');
        }
        return this.provider;
    }

    getSigner() {
        if (!this.signer) {
            throw new Error('Signer not initialized');
        }
        return this.signer;
    }

    async getNetwork() {
        return await this.provider.getNetwork();
    }
}